import React, { useState, useEffect, useRef } from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { FaUserCircle, FaChevronDown } from 'react-icons/fa';
import { fetchProfilePhoto } from '../services/api';
import { tokenService } from '../services/tokenService';
import { performLocalLogout } from '../auth/msalConfig';
import '../pages/shared/Dashboard.css';

const UserProfile: React.FC = () => {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const { t, i18n } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const languageDropdownRef = useRef<HTMLDivElement>(null);
  const currentLanguage = i18n.language || 'en';

  const languages = [
    { code: 'en', label: 'US' },
    { code: 'de', label: 'DE' },
  ];

  const currentLang = languages.find(lang => lang.code === currentLanguage) || languages[0];

  // Fetch profile photo from Microsoft Graph
  useEffect(() => {
    const getProfilePhoto = async () => {
      if (!account) return;
      try {
        const accessToken = await tokenService.getAccessToken(['User.Read']);
        const photoUrl = await fetchProfilePhoto(accessToken);
        setProfilePhoto(photoUrl);
      } catch (error) {
        setProfilePhoto(null);
      }
    };
    getProfilePhoto();
    // Only run when account changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  const handleLogout = () => {
    // Clear token refresh timer before logout
    tokenService.clearRefreshTimer();
    // Perform local logout without showing Microsoft account picker
    performLocalLogout(instance);
  };

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target as Node)) {
        setIsLanguageDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (!account) {
    return null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className="user-profile-btn"
      >
        {profilePhoto ? (
          <img
            src={profilePhoto}
            alt="Profile"
            className="user-profile-avatar"
          />
        ) : (
          FaUserCircle({ size: 36, color: '#0091d0', style: { marginRight: 8 } })
        )}
        <span className="user-profile-name" style={{ fontWeight: 500, color: '#222', marginRight: 8 }}>{account.name}</span>
        {FaChevronDown({ size: 18, color: '#222', style: { transition: 'transform 0.2s', transform: isDropdownOpen ? 'rotate(180deg)' : 'none' } })}
      </button>
      {isDropdownOpen && (
        <div className="user-profile-dropdown">
          <div className="user-profile-dropdown-header">
            {profilePhoto ? (
              <img
                src={profilePhoto}
                alt="Profile"
                className="user-profile-dropdown-avatar"
              />
            ) : (
              FaUserCircle({ size: 48, color: '#0091d0', style: { marginRight: 12 } })
            )}
            <div>
              <div className="user-profile-dropdown-name">{account.name}</div>
              <div className="user-profile-dropdown-email">{account.username}</div>
            </div>
          </div>
          <div className="user-profile-language-toggle">
            <div className="user-profile-language-label">{t('common.language')}</div>
            <div className="user-profile-language-selector" ref={languageDropdownRef}>
              <button
                className="user-profile-language-selector-btn"
                onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
              >
                <span className="user-profile-language-text">{currentLang.label}</span>
                {FaChevronDown({ 
                  size: 14, 
                  color: '#666', 
                  style: { 
                    transition: 'transform 0.2s', 
                    transform: isLanguageDropdownOpen ? 'rotate(180deg)' : 'none',
                    marginLeft: 'auto'
                  } 
                })}
              </button>
              {isLanguageDropdownOpen && (
                <div className="user-profile-language-dropdown">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      className={`user-profile-language-option ${currentLanguage === lang.code ? 'active' : ''}`}
                      onClick={() => {
                        handleLanguageChange(lang.code);
                        setIsLanguageDropdownOpen(false);
                      }}
                    >
                      <span className="user-profile-language-text">{lang.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="user-profile-signout-btn"
            onMouseOver={e => (e.currentTarget.style.background = '#ffeaea')}
            onMouseOut={e => (e.currentTarget.style.background = '#f5f5f5')}
          >
            {t('userProfile.signOut')}
          </button>
        </div>
      )}
    </div>
  );
};

export default UserProfile; 